import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FiMoreVertical } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { match } from 'ts-pattern';

import { ActionMenu, ActionMenuItem } from '@/components/actionMenu/actionMenu';
import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Icon } from '@/components/icon/icon';
import {
  evaluationsKeys, useDeleteSchedule, usePauseSchedule, useResumeSchedule
} from '@/hooks/queries/useEvaluations';
import { ScheduleResponse } from '@/types/evals';

type Props = {
  schedule: ScheduleResponse;
  // Display name of the schedule's source — used as the type-to-confirm value in the delete modal.
  sourceLabel: string;
};

// Per-row Actions menu for the evaluation schedule table. Menu contents depend on the
// schedule state: ACTIVE → Pause, PAUSED → Resume, DISABLED → Resume (grayed out, since
// the underlying block must clear first). Delete is always available.
// Items are text-only (no leading icon) per the Figma design.
// TODO(KANP-1410): per-eval role is not on the payload yet, so owner-only actions are
// shown unconditionally. Gate on the real role once it's available.
export function ScheduleActionMenu( { schedule, sourceLabel }: Props ) {
  const [ showDelete, setShowDelete ] = useState( false );
  const queryClient = useQueryClient();

  const pauseMutation = usePauseSchedule();
  const resumeMutation = useResumeSchedule();
  const deleteMutation = useDeleteSchedule();

  const handlePause = () => {
    pauseMutation.mutate( schedule.id, {
      onSuccess: () => toast.success( 'Schedule paused' ),
      onError: () => toast.error( 'Unable to pause schedule' )
    } );
  };

  const handleResume = () => {
    resumeMutation.mutate( schedule.id, {
      onSuccess: () => toast.success( 'Schedule resumed' ),
      onError: () => toast.error( 'Unable to resume schedule' )
    } );
  };

  const primaryItem = match( schedule.state )
    .with( 'ACTIVE', () => (
      <ActionMenuItem
        value="Pause"
        onClick={ handlePause }
        disabled={ pauseMutation.isPending }
        data-testid="schedule-action-pause"
      />
    ) )
    .with( 'PAUSED', () => (
      <ActionMenuItem
        value="Resume"
        onClick={ handleResume }
        disabled={ resumeMutation.isPending }
        data-testid="schedule-action-resume"
      />
    ) )
    .with( 'DISABLED', () => (
      <ActionMenuItem
        value="Resume"
        disabled
        data-testid="schedule-action-resume"
      />
    ) )
    .exhaustive();

  return (
    <>
      <ActionMenu
        icon={ <Icon icon={ FiMoreVertical } /> }
        data-testid={ `schedule-actions-${ schedule.id }` }
      >
        { primaryItem }
        <ActionMenuItem
          value="Delete"
          onClick={ () => setShowDelete( true ) }
          data-testid="schedule-action-delete"
        />
      </ActionMenu>
      <DeleteModal
        isOpen={ showDelete }
        onClose={ () => {
          setShowDelete( false );
          // Refetch only now that the modal is dismissed — invalidating earlier would
          // unmount this row (and the modal) before the success step could be seen.
          if ( deleteMutation.isSuccess ) {
            toast.success( 'Schedule deleted' );
            queryClient.invalidateQueries( { queryKey: [ ...evaluationsKeys.all, 'schedules' ] } );
          }
          deleteMutation.reset();
        } }
        onDelete={ () => deleteMutation.mutateAsync( schedule.id ) }
        entityName={ sourceLabel }
        entityType="schedule"
      />
    </>
  );
}
